/**
 * mapSync.ts
 * マップ同期・スワイプ連動関連メソッド
 * App.vue から切り出し
 */

import { osmBrightSources, osmBrightLayers } from '@/js/layers'
import { requestAddLayers } from '@/js/layerEngine/addLayersGate'

type VM = any

// ─── マップ同期 ───

export function startMapSync(vm: VM): void {
  if (vm.mapSyncEnabled) return

  const map01 = vm.$store.state.map01
  const map02 = vm.$store.state.map02

  if (!map01 || !map02) return

  let isSyncing = false
  let rafId: number | null = null
  let pending01: any = null
  let pending02: any = null

  const flushSync = () => {
    rafId = null
    vm.mapSyncHandlers.rafId = null
    // 連動モードでなければ何もしない
    if (!vm.$store.state.mapSyncMode) {
      pending01 = null
      pending02 = null
      return
    }
    if (vm.swipePanActive) {
      pending01 = null
      pending02 = null
      return
    }
    if (pending01 && !isSyncing) {
      isSyncing = true
      map02.jumpTo(pending01)
      isSyncing = false
    }
    if (pending02 && !isSyncing) {
      isSyncing = true
      map01.jumpTo(pending02)
      isSyncing = false
    }
    pending01 = null
    pending02 = null
  }
  const scheduleSync = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(flushSync)
      vm.mapSyncHandlers.rafId = rafId
    }
  }

  // map01の変更をmap02に同期（通常時のみ、連動モード時のみ）
  const syncMap01ToMap02 = () => {
    const zoomOffset = vm.printZoomOffset || 0
    const hasZoomOffset = zoomOffset !== 0
    if (isSyncing || vm.swipePanActive) return
    // 詳細/俯瞰モードでなければ、masterがmap02の時は同期しない
    if (!hasZoomOffset && vm.mapSyncMaster === 'map02') return
    // 連動モードでなければ同期しない
    if (!vm.$store.state.mapSyncMode) return
    // 詳細/俯瞰モードでmap02から同期中の場合、逆同期を防ぐ
    if (hasZoomOffset && pending02) return
    pending01 = {
      center: map01.getCenter(),
      zoom: map01.getZoom() - zoomOffset, // +オフセット = map02は広域（ズームアウト）
      bearing: map01.getBearing(),
      pitch: map01.getPitch()
    }
    scheduleSync()
  }

  // map02の変更をmap01に同期（スワイプ時は map02 を主にする、連動モード時のみ）
  const syncMap02ToMap01 = () => {
    // 詳細/俯瞰モード時はmap02操作でもmap01が追従
    const zoomOffset = vm.printZoomOffset || 0
    const hasZoomOffset = zoomOffset !== 0
    if (isSyncing || vm.swipePanActive) return
    // 詳細/俯瞰モードでなければ、masterがmap01の時は同期しない
    if (!hasZoomOffset && vm.mapSyncMaster === 'map01') return
    // 連動モードでなければ同期しない
    if (!vm.$store.state.mapSyncMode) return
    pending02 = {
      center: map02.getCenter(),
      zoom: map02.getZoom() + zoomOffset, // map02が広域なら、map01は詳細に戻す
      bearing: map02.getBearing(),
      pitch: map02.getPitch()
    }
    scheduleSync()
  }

  // 主従を決める（通常は操作側で切り替え。スワイプ中は map02 固定）
  const setMaster01 = () => { vm.mapSyncMaster = 'map01' }
  const setMaster02 = () => { vm.mapSyncMaster = 'map02' }
  map01.on('mousedown', setMaster01)
  map01.on('touchstart', setMaster01)
  map02.on('mousedown', setMaster02)
  map02.on('touchstart', setMaster02)

  map01.on('move', syncMap01ToMap02)
  map02.on('move', syncMap02ToMap01)

  vm.mapSyncHandlers.map01ToMap02 = syncMap01ToMap02
  vm.mapSyncHandlers.map02ToMap01 = syncMap02ToMap01
  vm.mapSyncHandlers.map01PointerDown = setMaster01
  vm.mapSyncHandlers.map01TouchStart = setMaster01
  vm.mapSyncHandlers.map02PointerDown = setMaster02
  vm.mapSyncHandlers.map02TouchStart = setMaster02
  vm.mapSyncHandlers.rafId = rafId
  vm.mapSyncEnabled = true
}

export function stopMapSync(vm: VM): void {
  if (!vm.mapSyncEnabled) return

  const map01 = vm.$store.state.map01
  const map02 = vm.$store.state.map02

  if (map01) {
    if (vm.mapSyncHandlers.map01ToMap02) map01.off('move', vm.mapSyncHandlers.map01ToMap02)
    if (vm.mapSyncHandlers.map01PointerDown) map01.off('mousedown', vm.mapSyncHandlers.map01PointerDown)
    if (vm.mapSyncHandlers.map01TouchStart) map01.off('touchstart', vm.mapSyncHandlers.map01TouchStart)
  }
  if (map02) {
    if (vm.mapSyncHandlers.map02ToMap01) map02.off('move', vm.mapSyncHandlers.map02ToMap01)
    if (vm.mapSyncHandlers.map02PointerDown) map02.off('mousedown', vm.mapSyncHandlers.map02PointerDown)
    if (vm.mapSyncHandlers.map02TouchStart) map02.off('touchstart', vm.mapSyncHandlers.map02TouchStart)
  }
  if (vm.mapSyncHandlers.rafId) {
    cancelAnimationFrame(vm.mapSyncHandlers.rafId)
  }

  vm.mapSyncHandlers.map01ToMap02 = null
  vm.mapSyncHandlers.map02ToMap01 = null
  vm.mapSyncHandlers.map01PointerDown = null
  vm.mapSyncHandlers.map01TouchStart = null
  vm.mapSyncHandlers.map02PointerDown = null
  vm.mapSyncHandlers.map02TouchStart = null
  vm.mapSyncHandlers.rafId = null
  vm.mapSyncEnabled = false
  // 念のため操作系を有効化
  try { map01?.scrollZoom?.enable(); map01?.doubleClickZoom?.enable(); map01?.touchZoomRotate?.enable(); map01?.dragPan?.enable(); map01?.keyboard?.enable(); map01?.touchZoomRotate?.disableRotation && map01.touchZoomRotate.disableRotation() } catch (_) {}
  try { map02?.scrollZoom?.enable(); map02?.doubleClickZoom?.enable(); map02?.touchZoomRotate?.enable(); map02?.dragPan?.enable(); map02?.keyboard?.enable(); map02?.touchZoomRotate?.disableRotation && map02.touchZoomRotate.disableRotation() } catch (_) {}
}

// マップ連動/個別モードの切り替え
export function toggleMapSyncMode(vm: VM): void {
  const newMode = !vm.$store.state.mapSyncMode
  vm.$store.state.mapSyncMode = newMode

  // 連動モードに切り替えた場合、map01の座標に全マップを合わせる
  if (newMode) {
    syncAllMapsToMap01(vm)
  } else {
    // 個別モードに切り替えた場合、各マップの現在状態を保存
    saveAllMapStates(vm)
  }

  // パーマリンク更新をトリガー
  vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire
}

// 全マップをmap01に合わせる
export function syncAllMapsToMap01(vm: VM): void {
  const map01 = vm.$store.state.map01
  if (!map01) return

  const center = map01.getCenter()
  const zoom = map01.getZoom()
  const bearing = map01.getBearing()
  const pitch = map01.getPitch()

  // 4画面モード または 2画面モード
  const mapNames = vm.is4ScreenMode
    ? ['map02', 'map03', 'map04']
    : (vm.s_map2Flg ? ['map02'] : [])

  mapNames.forEach((mapName: string) => {
    const map = vm.$store.state[mapName]
    if (map) {
      map.jumpTo({ center, zoom, bearing, pitch })
    }
  })
}

// 全マップの現在状態を保存
export function saveAllMapStates(vm: VM): void {
  // 4画面モード または 2画面モード
  const mapNames = vm.is4ScreenMode
    ? ['map01', 'map02', 'map03', 'map04']
    : (vm.s_map2Flg ? ['map01', 'map02'] : ['map01'])

  mapNames.forEach((mapName: string) => {
    const map = vm.$store.state[mapName]
    if (map) {
      const center = map.getCenter()
      vm.$store.state.mapIndividualStates[mapName] = {
        lng: center.lng,
        lat: center.lat,
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing()
      }
    }
  })
}

// ─── スワイプ連動 ───

export function enableSwipePan(vm: VM): void {
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  const container = document.getElementById('map00')
  if (!mapA || !mapB || !container) return

  try { mapA.dragPan.disable(); mapB.dragPan.disable(); } catch (_) {}

  let isDragging = false
  let lastX = 0
  let lastY = 0

  const isHandle = (target: any) => {
    return !!(target && (target.closest && (target.closest('.swipe-handle') || target.closest('.swipe-close-btn'))))
  }

  const onPointerDown = (e: PointerEvent) => {
    if (!vm.swipeMode) return
    if (isHandle(e.target)) return
    isDragging = true
    vm.swipePanActive = true
    lastX = e.clientX
    lastY = e.clientY
    try { container.setPointerCapture(e.pointerId) } catch (_) {}
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!isDragging) return
    const dx = lastX - e.clientX
    const dy = lastY - e.clientY
    lastX = e.clientX
    lastY = e.clientY
    try {
      mapA.panBy([dx, dy], { animate: false })
      mapB.panBy([dx, dy], { animate: false })
    } catch (_) {}
    e.preventDefault()
  }

  const endDrag = (e: PointerEvent) => {
    if (!isDragging) return
    isDragging = false
    vm.swipePanActive = false
    try { container.releasePointerCapture(e.pointerId) } catch (_) {}
  }

  container.addEventListener('pointerdown', onPointerDown)
  container.addEventListener('pointermove', onPointerMove)
  container.addEventListener('pointerup', endDrag)
  container.addEventListener('pointercancel', endDrag)
  container.addEventListener('pointerleave', endDrag)

  vm.swipePanHandlers = {
    container,
    onPointerDown,
    onPointerMove,
    endDrag,
  }
}

export function disableSwipePan(vm: VM): void {
  // 既存のカスタムパンは使っていないが、念のためクリーンアップ
  const h = vm.swipePanHandlers
  if (h && h.container) {
    h.container.removeEventListener('pointerdown', h.onPointerDown)
    h.container.removeEventListener('pointermove', h.onPointerMove)
    h.container.removeEventListener('pointerup', h.endDrag)
    h.container.removeEventListener('pointercancel', h.endDrag)
    h.container.removeEventListener('pointerleave', h.endDrag)
  }
  vm.swipePanHandlers = null
  vm.swipePanActive = false
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  try { mapA && mapA.dragPan.enable() } catch (_) {}
  try { mapB && mapB.dragPan.enable() } catch (_) {}
}

export function enableSwipeNudge(vm: VM): void {
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  if (!mapA || !mapB) return

  const nudge = () => {
    if (!vm.swipeMode) return
    vm.swipeNudgeScheduled = false
    const zA = mapA.getZoom()
    const zB = mapB.getZoom()
    try { mapA.setZoom(zA + 0.000001); mapA.setZoom(zA) } catch (_) {}
    try { mapB.setZoom(zB + 0.000001); mapB.setZoom(zB) } catch (_) {}
  }

  const scheduleNudge = () => {
    if (vm.swipeNudgeScheduled) return
    vm.swipeNudgeScheduled = true
    requestAnimationFrame(nudge)
  }

  const onMoveEndA = () => scheduleNudge()
  const onMoveEndB = () => scheduleNudge()

  mapA.on('moveend', onMoveEndA)
  mapB.on('moveend', onMoveEndB)

  vm.swipeNudgeHandlers = {
    onMoveEndA,
    onMoveEndB,
  }
}

export function disableSwipeNudge(vm: VM): void {
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  const h = vm.swipeNudgeHandlers
  if (h) {
    try { mapA && mapA.off('moveend', h.onMoveEndA) } catch (_) {}
    try { mapB && mapB.off('moveend', h.onMoveEndB) } catch (_) {}
  }
  vm.swipeNudgeHandlers = null
  vm.swipeNudgeScheduled = false
}

export function enableSwipeManualPan(vm: VM): void {
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  const container = document.getElementById('map02')
  if (!mapA || !mapB || !container) return

  // ネイティブのdragPanは無効化し、手動でpanByする
  try { mapB.dragPan.disable() } catch (_) {}

  let dragging = false
  let lastX = 0
  let lastY = 0

  const isHandle = (target: any) => {
    return !!(target && (target.closest && (target.closest('.swipe-handle') || target.closest('.swipe-close-btn'))))
  }

  const onPointerDown = (e: PointerEvent) => {
    if (!vm.swipeMode) return
    if (isHandle(e.target)) return
    dragging = true
    lastX = e.clientX
    lastY = e.clientY
    try { container.setPointerCapture(e.pointerId) } catch (_) {}
    e.preventDefault()
  }
  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) return
    const dx = lastX - e.clientX
    const dy = lastY - e.clientY
    lastX = e.clientX
    lastY = e.clientY
    try { mapB.panBy([dx, dy], { animate: false }) } catch (_) {}
    try { mapA.panBy([dx, dy], { animate: false }) } catch (_) {}
    e.preventDefault()
  }
  const endDrag = (e: PointerEvent) => {
    if (!dragging) return
    dragging = false
    try { container.releasePointerCapture(e.pointerId) } catch (_) {}
  }

  container.addEventListener('pointerdown', onPointerDown)
  container.addEventListener('pointermove', onPointerMove)
  container.addEventListener('pointerup', endDrag)
  container.addEventListener('pointercancel', endDrag)
  container.addEventListener('pointerleave', endDrag)

  vm.swipeManualPanHandlers = {
    container,
    onPointerDown,
    onPointerMove,
    endDrag,
  }
}

export function disableSwipeManualPan(vm: VM): void {
  const h = vm.swipeManualPanHandlers
  if (h && h.container) {
    h.container.removeEventListener('pointerdown', h.onPointerDown)
    h.container.removeEventListener('pointermove', h.onPointerMove)
    h.container.removeEventListener('pointerup', h.endDrag)
    h.container.removeEventListener('pointercancel', h.endDrag)
    h.container.removeEventListener('pointerleave', h.endDrag)
  }
  vm.swipeManualPanHandlers = null
  const mapA = vm.$store.state.map01
  const mapB = vm.$store.state.map02
  // 手動パンで無効化した操作を両方とも戻す
  try { mapA && mapA.dragPan.enable() } catch (_) {}
  try { mapB && mapB.dragPan.enable() } catch (_) {}
  try { mapA && mapA.scrollZoom.enable() } catch (_) {}
  try { mapB && mapB.scrollZoom.enable() } catch (_) {}
  try { mapA && mapA.doubleClickZoom.enable() } catch (_) {}
  try { mapB && mapB.doubleClickZoom.enable() } catch (_) {}
  try {
    if (mapA && mapA.touchZoomRotate) {
      mapA.touchZoomRotate.enable()
      mapA.touchZoomRotate.disableRotation && mapA.touchZoomRotate.disableRotation()
    }
    if (mapB && mapB.touchZoomRotate) {
      mapB.touchZoomRotate.enable()
      mapB.touchZoomRotate.disableRotation && mapB.touchZoomRotate.disableRotation()
    }
  } catch (_) {}
}

export function forceEnableMapInteractions(vm: VM, map: any): void {
  if (!map) return
  try { map.scrollZoom.enable() } catch (_) {}
  try { map.doubleClickZoom.enable() } catch (_) {}
  try { map.touchZoomRotate.enable() } catch (_) {}
  try { map.dragPan.enable() } catch (_) {}
  try { map.keyboard && map.keyboard.enable() } catch (_) {}
  try {
    if (map.touchZoomRotate && map.touchZoomRotate.disableRotation) {
      map.touchZoomRotate.disableRotation()
    }
  } catch (_) {}
}

export function setMapInteractions(vm: VM, map: any, enabled: boolean): void {
  if (!map) return
  try { enabled ? map.dragPan.enable() : map.dragPan.disable() } catch (_) {}
  try { enabled ? map.scrollZoom.enable() : map.scrollZoom.disable() } catch (_) {}
  try { enabled ? map.doubleClickZoom.enable() : map.doubleClickZoom.disable() } catch (_) {}
  try { enabled ? map.keyboard.enable() : map.keyboard.disable() } catch (_) {}
  try {
    if (map.touchZoomRotate) {
      enabled ? map.touchZoomRotate.enable() : map.touchZoomRotate.disable()
      map.touchZoomRotate.disableRotation()
    }
  } catch (_) {}
}

export function enableSwipeFollow(vm: VM): void {
  const map01 = vm.$store.state.map01
  const map02 = vm.$store.state.map02
  if (!map01 || !map02) return

  let rafId: number | null = null
  let pending: any = null
  const flush = () => {
    rafId = null
    if (!pending || !vm.swipeMode) return
    try { map01.jumpTo(pending) } catch (_) {}
  }
  const schedule = () => {
    if (rafId) return
    rafId = requestAnimationFrame(flush)
    vm.swipeFollowHandlers.rafId = rafId
  }
  const onMove = () => {
    if (!vm.swipeMode) return
    pending = {
      center: map02.getCenter(),
      zoom: map02.getZoom(),
      bearing: map02.getBearing(),
      pitch: map02.getPitch()
    }
    schedule()
  }
  map02.on('move', onMove)
  vm.swipeFollowHandlers.move = onMove
}

export function disableSwipeFollow(vm: VM): void {
  const map01 = vm.$store.state.map01
  const map02 = vm.$store.state.map02
  const h = vm.swipeFollowHandlers
  if (map02 && h.move) {
    try { map02.off('move', h.move) } catch (_) {}
  }
  if (h.rafId) {
    cancelAnimationFrame(h.rafId)
  }
  vm.swipeFollowHandlers = { move: null, rafId: null }
  // 念のため map01 にジャンプ中のペンディングをクリア
  if (map01) {
    try { map01.stop && map01.stop() } catch (_) {}
  }
}

// map02のレイヤーが空だったらOSMベクターを追加
export function addOsmToMap02(vm: VM): void {
  const map02Layers = vm.s_selectedLayers?.map02 || []

  // 既にレイヤーがある場合は何もしない
  if (map02Layers.length > 0) return

  // OSMベクターレイヤーを追加
  const osmLayer = {
    id: 'oh-vector-layer-osm-bright',
    label: 'OSMベクター',
    sources: osmBrightSources,
    layers: osmBrightLayers,
    opacity: 1,
    visibility: true,
    attribution: '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
  }

  vm.s_selectedLayers.map02.unshift(osmLayer)
  // addLayersRequestedカウンターをインクリメントしないとaddLayers()がスキップされる
  requestAddLayers(vm.$store, 'map02')
}
